import * as React from "react";
import { IData, defaultClientItem } from "../../../store/client/clientItem";
import { Card, CardActions, CardContent, CardHeader, Grid, Typography } from "@material-ui/core";
import { ClientIcon } from "../../atoms/icons/ClientIcon";
import { Skeleton } from "@material-ui/lab";
import { ContactInfo } from "../../molecules/views/ContactInfo";
import { Adres } from "../../molecules/views/Adres";
import { Naam } from "../../molecules/views/Naam";
import { RijksregisterNummer } from "../../molecules/views/RijksregisterNummer";
import { GeboorteDatum } from "../../molecules/views/GeboorteDatum";
import { Leeftijd } from "../../molecules/views/Leeftijd";
import { LeeftijdsCategorie } from "../../molecules/views/LeeftijdsCategorie";
import { Geslacht } from "../../molecules/views/Geslacht";
import { Overleden } from "../../molecules/views/Overleden";
import { Taal } from "../../molecules/views/Taal";
import { Opmerkingen } from "../../molecules/views/Opmerkingen";
import { Activiteit } from "../../molecules/views/Activiteit";
import { Beperking } from "../../molecules/views/Beperking";
import { BeperkingTypes } from "../../molecules/views/BeperkingTypes";
import { Problemen } from "../../molecules/views/Problemen";
import { Contactpersoon } from "../../molecules/views/Contactpersoon";
import { ContactPersons } from "../../molecules/views/ContactPersons";
import { Contactpunten } from "../../molecules/views/Contactpunten";
import { Files } from "../../molecules/views/Files";
import { useTranslation } from 'react-i18next';
import { IDetailOrganism } from "../IDetailOrganism";
import { CreateFileButton } from "../../atoms/buttons/CreateFileButton";
import { useDispatch } from "react-redux";
import { RootActionCreators } from "../../../store";

// All the incoming values
interface IProps {

}

// Outgoing functions
interface IDispatch {

}

// Actual Component
export type AllProps = IProps & IDispatch & IDetailOrganism<IData>;
export const ClientDetail = (props: AllProps) => {
    const { t: tClient } = useTranslation('client');
    const dispatch = useDispatch();
    const data = props.data?.streamId ? props.data : defaultClientItem;

    // Handler for creating a new file
    const handleCreateFile = () => {
        if (data.streamId && data.uuid) {
            dispatch(RootActionCreators.Navigate.ToFileNew(data.streamId, data.uuid));
        }
    };

    // Helper function to check if a value should be displayed
    const hasValue = (value: any): boolean => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string' && value.trim() === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
    };

    return (<Card>
        {props.loading ? 
            <CardHeader 
                title={<Skeleton variant="text" />} 
                subheader={<Skeleton variant="text" />} 
                avatar={<Skeleton variant="circle" />} 
            /> : 
            <CardHeader 
                title={tClient('detailTitle', data)} 
                subheader={`Client nummer: ${data.streamId}`} 
                avatar={<ClientIcon />} 
            /> 
        }
        <CardContent>
            <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">

                {/* Blok 1: Cliëntgegevens */}
                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tClient('clientDataTitle')}
                    </Typography>

                    {(hasValue(data.firstName) || hasValue(data.lastName)) && (
                        <Naam 
                            loading={props.loading} 
                            firstName={data.firstName}
                            lastName={data.lastName}
                            label={tClient('nameLabel')}
                        />
                    )}

                    {hasValue(data.burgerServiceNumber) && (
                        <RijksregisterNummer 
                            loading={props.loading}
                            value={data.burgerServiceNumber}
                        />
                    )}

                    {hasValue(data.birthDate) && (
                        <GeboorteDatum 
                            loading={props.loading}
                            value={data.birthDate}
                        />
                    )}

                    {hasValue(data.birthDate) && (
                        <Leeftijd 
                            loading={props.loading}
                            birthDate={data.birthDate}
                        />
                    )}

                    {hasValue(data.birthDate) && (
                        <LeeftijdsCategorie 
                            loading={props.loading}
                            birthDate={data.birthDate}
                        />
                    )}

                    {hasValue(data.gender) && (
                        <Geslacht 
                            loading={props.loading}
                            value={data.gender}
                        />
                    )}

                    {data.isDeceased && (
                        <Overleden 
                            loading={props.loading}
                            value={data.isDeceased}
                        />
                    )}

                    {hasValue(data.languageName) && (
                        <Taal 
                            loading={props.loading}
                            value={data.languageName}
                        />
                    )}

                    {hasValue(data.remarks) && (
                        <Opmerkingen 
                            loading={props.loading}
                            value={data.remarks}
                        />
                    )}
                </Grid>

                {/* Blok 2: Contactgegevens */}
                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tClient('contactDataTitle')}
                    </Typography>
                    
                    {(hasValue(data.officialStreet) || hasValue(data.officialStreetNumber) || hasValue(data.officialCityName)) && (
                        <Adres
                            loading={props.loading}
                            label={tClient('officialCityLabel').split(' - ')[0]} 
                            street={data.officialStreet}
                            houseNumber={data.officialStreetNumber}
                            city={data.officialCityName}
                            postalCode={data.officialCityPostalCode}
                        />
                    )}

                    {(hasValue(data.residencyStreet) || hasValue(data.residencyStreetNumber) || hasValue(data.residencyCityName)) && (
                        <Adres
                            loading={props.loading}
                            label={tClient('residencyCityLabel').split(' - ')[0]}
                            street={data.residencyStreet}
                            houseNumber={data.residencyStreetNumber}
                            city={data.residencyCityName}
                            postalCode={data.residencyCityPostalCode}
                        />
                    )}

                    {hasValue(data.homePhone) && (
                        <ContactInfo
                            loading={props.loading}
                            type="phone"
                            label={tClient('homePhoneLabel')}
                            value={data.homePhone}
                        />
                    )}

                    {hasValue(data.workPhone) && (
                        <ContactInfo
                            loading={props.loading}
                            type="phone"
                            label={tClient('otherPhoneLabel')}
                            value={data.workPhone}
                        />
                    )}

                    {hasValue(data.mobilePhone) && (
                        <ContactInfo
                            loading={props.loading}
                            type="mobile"
                            label={tClient('mobilePhoneLabel')}
                            value={data.mobilePhone}
                        />
                    )}

                    {hasValue(data.email) && (
                        <ContactInfo
                            loading={props.loading}
                            type="email"
                            label={tClient('emailLabel')}
                            value={data.email}
                        />
                    )}

                    {hasValue(data.contactPersons) && (
                        <ContactPersons
                            loading={props.loading}
                            value={data.contactPersons}
                            label={tClient('contactPersonsTitle')}
                        />
                    )}

                    {hasValue(data.contactPerson) && (
                        <Contactpersoon
                            loading={props.loading}
                            value={data.contactPerson}
                            label={tClient('contactPersonLabel')}
                        />
                    )}
                </Grid>

                {/* Blok 3: Beperkingen en bijkomende uitdagingen */}
                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tClient('disabilityDataTitle')}
                    </Typography>

                    {hasValue(data.activityTypeName) && (
                        <Activiteit
                            loading={props.loading}
                            value={data.activityTypeName}
                        />
                    )}

                    {(hasValue(data.disabilityConfirmed) || hasValue(data.disabilitySuspected)) && (
                        <Beperking
                            loading={props.loading}
                            disabilityConfirmed={data.disabilityConfirmed}
                            disabilitySuspected={data.disabilitySuspected}
                        />
                    )}

                    {hasValue(data.disabilityTypes) && (
                        <BeperkingTypes
                            loading={props.loading}
                            value={data.disabilityTypes}
                        />
                    )}

                    {hasValue(data.problems) && (
                        <Problemen
                            loading={props.loading}
                            value={data.problems}
                        />
                    )}
                </Grid>

                {/* Blok 4: Nabijgelegen contactpunten */}
                <Grid item xs={12} md={12} lg={6}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tClient('nearbyContactPointsTitle')}
                    </Typography>

                    <Contactpunten
                        loading={props.loading}
                        cityId={data.residencyCityId || data.officialCityId}
                        hideIcon={true}
                        hideLabel={true}
                    />
                </Grid>

                {/* Blok 5: Dossiers */}
                <Grid item xs={12} md={12} lg={6}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tClient('filesTitle')}
                    </Typography>

                    <Files
                        loading={props.loading}
                        clientId={data.streamId}
                        hideIcon={true}
                        hideLabel={true}
                    />

                    <CreateFileButton 
                        onClick={handleCreateFile}
                        disabled={props.loading || !data.streamId || !data.uuid}
                    />
                </Grid>

            </Grid>
        </CardContent>
        <CardActions>
            {props.actions}
        </CardActions>
    </Card>)
}

